import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import PhotoUpload from './PhotoUpload';
import PhotoGallery from './PhotoGallery';

const ItemTypes = { ITEM: 'item' };

// Items created within this window keep their action buttons on screen; older
// ones tuck them behind the subtle toggle to keep an established tree quiet.
const RECENT_WINDOW_MS = 2 * 24 * 60 * 60 * 1000;

// Containers shallower than this open on load, so the shape of the inventory is
// visible without unfolding everything: depth 0, 1 and 2 expand, revealing
// items down to depth 3.
const INITIAL_EXPAND_DEPTH = 3;

const TreeItem = ({ item, allItems, depth = 0, moveItem, updateItem, deleteItem, addItem, onArrange, onPhotoAdded, onPhotoRemoved, onError, focusItemId, focusAncestorIds, onClearFocus }) => {
  const children = allItems
    .filter(child => child.locationId === item.id)
    .sort((a, b) => a.order - b.order);
  const photoCount = (item.photos || []).length;
  // Children or photos both make an item unfoldable, so both earn a caret.
  const hasContent = children.length > 0 || photoCount > 0;

  const isFocused = focusItemId === item.id;
  const isOnFocusPath = focusAncestorIds && focusAncestorIds.has(item.id);
  // Items predating the createdAt field have no timestamp — treat them as old.
  const isRecent = item.createdAt != null && Date.now() - item.createdAt < RECENT_WINDOW_MS;

  // Leaves stay shut at any depth: unfolding one only reveals its photo
  // gallery, which would bury the tree on load.
  const [isOpen, setIsOpen] = useState(
    () => children.length > 0 && depth < INITIAL_EXPAND_DEPTH
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [actionsRevealed, setActionsRevealed] = useState(false);
  const [editedName, setEditedName] = useState(item.name);
  const [editedDescription, setEditedDescription] = useState(item.description);
  const [newChildName, setNewChildName] = useState('');
  const addInputRef = useRef(null);
  const itemRef = useRef(null);

  const showActions = isRecent || actionsRevealed;

  // Ensure ancestors of the focused item are expanded
  useEffect(() => {
    if (isOnFocusPath) {
      setIsOpen(true);
    }
  }, [isOnFocusPath]);

  // Scroll focused item into view and flash highlight
  useEffect(() => {
    if (isFocused && itemRef.current) {
      // Small delay to let the tree expand first
      const timer = setTimeout(() => {
        itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isFocused]);

  // Sync edit fields when item changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditedName(item.name);
      setEditedDescription(item.description);
    }
  }, [item.name, item.description, isEditing]);

  useEffect(() => {
    if (isAdding && addInputRef.current) {
      addInputRef.current.focus();
    }
  }, [isAdding]);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.ITEM,
    item: { id: item.id },
    end: (draggedItem, monitor) => {
      const dropResult = monitor.getDropResult();
      if (draggedItem && dropResult) {
        moveItem(draggedItem.id, dropResult.targetId);
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.ITEM,
    drop: (draggedItem, monitor) => {
      if (draggedItem.id === item.id) return;
      if (monitor.isOver({ shallow: true })) {
        return { targetId: item.id };
      }
      return undefined;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  }));

  const combinedRef = useCallback((node) => {
    drag(drop(node));
    itemRef.current = node;
  }, [drag, drop]);

  const handleSave = () => {
    updateItem(item.id, editedName, editedDescription);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedName(item.name);
    setEditedDescription(item.description);
    setIsEditing(false);
  };

  const handleAddChild = (e) => {
    e.preventDefault();
    if (!newChildName.trim()) return;
    addItem({ name: newChildName.trim(), locationId: item.id, description: '' });
    setNewChildName('');
    setIsAdding(false);
    setIsOpen(true);
  };

  const depthColors = ['#6c757d', '#0d6efd', '#198754', '#dc3545', '#fd7e14', '#6f42c1'];
  const borderColor = depthColors[depth % depthColors.length];

  return (
    <li
      ref={combinedRef}
      className={`tree-item ${isOver ? 'tree-item-over' : ''} ${isFocused ? 'tree-item-focused' : ''}`}
      style={{
        opacity: isDragging ? 0.4 : 1,
        borderLeftColor: borderColor,
      }}
    >
      <div className="tree-item-content">
        {isEditing ? (
          <div className="d-flex flex-column w-100">
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
            />
            <textarea
              className="form-control form-control-sm mb-2"
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              rows="2"
            />
            <div>
              <button className="btn btn-primary btn-sm me-2" onClick={handleSave}>Save</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={handleCancel}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div className="tree-item-info" onClick={() => setIsOpen(!isOpen)} style={{ cursor: 'pointer' }}>
              <div className="d-flex align-items-center">
                <span className="tree-toggle me-1">
                  {hasContent ? (isOpen ? '▼' : '▶') : <span style={{ width: '1em', display: 'inline-block' }}></span>}
                </span>
                <strong>{item.name}</strong>
                <span className="text-muted ms-2" style={{ fontSize: '0.75rem' }}>#{item.id}</span>
                {children.length > 0 && (
                  <span className="badge bg-secondary rounded-pill ms-2">{children.length}</span>
                )}
                {photoCount > 0 && (
                  <span
                    className="text-muted ms-2"
                    style={{ fontSize: '0.75rem' }}
                    title={`${photoCount} photo${photoCount > 1 ? 's' : ''}`}
                  >
                    <i className="fas fa-camera"></i> {photoCount}
                  </span>
                )}
              </div>
              {item.description && <p className="tree-item-desc mb-0">{item.description}</p>}
            </div>
            <div className={`tree-item-actions${showActions ? '' : ' tree-item-actions-collapsed'}`}>
              {isFocused && (
                <button className="btn btn-outline-warning btn-sm" onClick={onClearFocus} title="Clear highlight">
                  <i className="fas fa-times"></i>
                </button>
              )}
              {!isRecent && (
                <button
                  className="tree-item-actions-toggle"
                  onClick={() => setActionsRevealed(!actionsRevealed)}
                  title={showActions ? 'Hide actions' : 'Show actions'}
                  aria-expanded={showActions}
                >
                  <i className="fas fa-ellipsis"></i>
                </button>
              )}
              {showActions && (
                <>
                  <button className="btn btn-outline-success btn-sm" onClick={() => setIsAdding(!isAdding)} title="Add item here">
                    <i className="fas fa-plus"></i>
                  </button>
                  {children.length > 0 && (
                    <button className="btn btn-outline-info btn-sm" onClick={() => onArrange(item.id)} title="Arrange contents">
                      <i className="fas fa-table-cells-large"></i>
                    </button>
                  )}
                  <PhotoUpload item={item} onPhotoAdded={onPhotoAdded} onError={onError} />
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    title="Sell on StorageLoot"
                    onClick={() => {
                      const params = new URLSearchParams({
                        title: item.name,
                        description: item.description || '',
                        photos: JSON.stringify(item.photos || []),
                        sourceItemId: item.id,
                      });
                      window.open(`https://storageloot.shop/import?${params.toString()}`, '_blank');
                    }}
                  >
                    &#x1F4B2;
                  </button>
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => setIsEditing(true)} title="Edit">
                    <i className="fas fa-edit"></i>
                  </button>
                  <button className="btn btn-outline-danger btn-sm" onClick={() => deleteItem(item.id)} title="Delete">
                    <i className="fas fa-trash"></i>
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {isAdding && (
        <form onSubmit={handleAddChild} className="d-flex gap-2 mt-2">
          <input
            ref={addInputRef}
            type="text"
            className="form-control form-control-sm"
            placeholder={`Add item inside ${item.name}...`}
            value={newChildName}
            onChange={(e) => setNewChildName(e.target.value)}
          />
          <button type="submit" className="btn btn-success btn-sm" disabled={!newChildName.trim()}>Add</button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setIsAdding(false); setNewChildName(''); }}>
            <i className="fas fa-times"></i>
          </button>
        </form>
      )}

      {isOpen && (
        <PhotoGallery item={item} onPhotoRemoved={onPhotoRemoved} onError={onError} />
      )}

      {isOpen && children.length > 0 && (
        <ul className="list-group tree-children">
          {children.map(child => (
            <TreeItem
              key={child.id}
              item={child}
              allItems={allItems}
              depth={depth + 1}
              moveItem={moveItem}
              updateItem={updateItem}
              deleteItem={deleteItem}
              addItem={addItem}
              onArrange={onArrange}
              onPhotoAdded={onPhotoAdded}
              onPhotoRemoved={onPhotoRemoved}
              onError={onError}
              focusItemId={focusItemId}
              focusAncestorIds={focusAncestorIds}
              onClearFocus={onClearFocus}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default TreeItem;
